import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import ReactTable from "@components/ReactTable";
import StatusButton from "@modules/Trees/components/Button/StatusButton";

export default function SensorList({ sensors }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const sid = query.get("selectedSensor");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [ordering, setOrdering] = useState("");

  const columns = useMemo(
    () => [
      {
        Header: `${t("sensor")}`,
        accessor: "name",
      },
      {
        Header: `${t("tree")}`,
        accessor: "tree",
      },
      {
        Header: `${t("battery")}`,
        accessor: "battery",
        Cell: (props) => {
          const battery = props.row.original.battery;
          if (battery === null || battery === undefined) return "-";
          return `${battery}%`;
        },
      },
      {
        Header: `${t("status")}`,
        accessor: "status",
        Cell: (props) => {
          const status = props.row.original.status;
          return (
            <StatusButton status={status} width="fit-content" padding="1px 10px">
              {status === 0 ? t("active") : t("inactive")}
            </StatusButton>
          );
        },
      },
    ],
    [t]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const filteredSensors = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return sensors;

    return sensors.filter((sensor) => {
      const haystack = [sensor.name, sensor.tree, sensor.hardware_serial, sensor.imei]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [sensors, search]);

  const sortedSensors = useMemo(() => {
    if (!ordering) return filteredSensors;

    const isDesc = ordering.startsWith("-");
    const key = isDesc ? ordering.slice(1) : ordering;
    const direction = isDesc ? -1 : 1;

    return [...filteredSensors].sort((a, b) => {
      const aValue = a?.[key] ?? "";
      const bValue = b?.[key] ?? "";

      if (typeof aValue === "number" && typeof bValue === "number") {
        return (aValue - bValue) * direction;
      }

      return (
        String(aValue).localeCompare(String(bValue), undefined, {
          numeric: true,
          sensitivity: "base",
        }) * direction
      );
    });
  }, [filteredSensors, ordering]);

  const pagedSensors = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedSensors.slice(start, start + pageSize);
  }, [sortedSensors, page, pageSize]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(sortedSensors.length / pageSize));
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [sortedSensors.length, pageSize, page]);

  const onRowClicked = (row) => {
    const { id } = row.original;
    const params = new URLSearchParams(location.search);
    params.set("selectedSensor", id);
    navigate(`${location.pathname}?${params.toString()}`);
  };

  return (
    <ReactTable
      columns={columns}
      data={pagedSensors}
      onRowClicked={onRowClicked}
      activeId={sid}
      pagesize={pageSize}
      size={[10, 20, 30, 40, 50]}
      manualPagination
      pageCount={Math.max(1, Math.ceil(sortedSensors.length / pageSize))}
      currentPage={page}
      onPageChange={setPage}
      onPageSizeChange={(nextPageSize) => {
        setPageSize(nextPageSize);
        setPage(1);
      }}
      onSortChange={(nextOrdering) => {
        setOrdering(nextOrdering);
        setPage(1);
      }}
      searchValue={searchInput}
      onSearchChange={setSearchInput}
      searchPlaceholder={t("search_sensor")}
    />
  );
}
